import { useMemo, useState } from 'react'
import { Button, Form, InputNumber, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatMoneyValue } from '@/shared/lib/money'
import { DataTable } from '@/shared/ui/table/DataTable'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useEmployeeNodes } from '@/features/hr/useEmployeeNodes'
import { PayrollGenerateModal } from '@/features/hr/PayrollGenerateModal'
import { PAYROLL_STATE_COLOR } from '@/features/hr/leaveState'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  PAYROLL_RUN_STATES,
  costCenterNodes,
  fetchPayrollRunPage,
  isSelfApproval,
  periodLabel,
  type PayrollRun,
  type PayrollRunState,
} from '@/features/hr/payrollApi'

/**
 * Vedomostlar ro'yxati.
 *
 * <h2>⚠️ HOLAT FILTRI FAQAT TANISH QIYMATLARNI YUBORADI</h2>
 * Server `state` ni XOM holda SQL ga uzatadi va tanimasa BO'SH sahifa
 * qaytaradi, 400 EMAS. Erkin matn maydoni bo'lsa operator «Loyiha» deb
 * yozib, bo'sh ro'yxatni «vedomost yo'q» deb o'qirdi.
 *
 * <h2>⚠️ «O'ZIM YIG'GANMAN» BELGISI RO'YXATDA HAM BOR</h2>
 * Tasdiqlash qoidasi shu faktga tayanadi (`PayrollDecisionModal`).
 * Belgini faqat oynada ko'rsatish operatorga ro'yxatda «qaysi birini
 * hamkasbim tasdiqlashi kerak» degan savolga javob bermasdi.
 *
 * <h2>⚠️ DOIRA — `OWN` YO'Q</h2>
 * `HrScope.PAYROLL_RUN` faqat `SUBTREE`: vedomost FILIALning hujjati.
 * `OWN` doirali xodim bu sahifada hech narsa ko'rmaydi va bu to'g'ri —
 * uning o'z varaqasi kartochkasida.
 *
 * <h2>⚠️ YIL VA OY — FILTR PANELIDA, va bu tezlik masalasi ham</h2>
 * Ular `InputNumber` da tashqarida turganda «2026» ni terish
 * `2 → 20 → 202 → 2026` bo'lib TO'RTTA so'rov yuborardi, ulardan
 * uchtasi ma'nosiz («2-yil» degan vedomost yo'q). Panel ichida qiymat
 * qoralamada yotadi va serverga faqat «Qo'llash» dan keyin BIR MARTA
 * boradi.
 *
 * <h2>⚠️ Filtr endi MANZILDA</h2>
 * Ilgari u komponent ichidagi `useState` da edi: sahifa yangilanganda
 * yo'qolardi va filtrlangan ro'yxatning havolasini yuborib bo'lmasdi.
 */
type Filters = TableState['filters']

const YEAR_MIN = 2000
const YEAR_MAX = 2100

/**
 * ⚠️ Havoladagi qiymat TEKSHIRILADI. Server `state` ni xom holda SQL ga
 * uzatadi va tanimasa BO'SH sahifa qaytaradi, 400 emas — ya'ni qo'lda
 * tahrirlangan yoki eskirgan havola «vedomost yo'q» degan yolg'on javob
 * berardi.
 */
function runStateFilter(values: readonly string[] | undefined): PayrollRunState | null {
  const raw = values?.[0]
  return PAYROLL_RUN_STATES.find((value) => value === raw) ?? null
}

function intFilter(values: readonly string[] | undefined, min: number, max: number): number | null {
  const raw = values?.[0]
  if (raw === undefined) return null
  const parsed = Number.parseInt(raw, 10)
  return Number.isInteger(parsed) && parsed >= min && parsed <= max ? parsed : null
}

export function PayrollRunsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { can } = usePermissions()

  // ⚠️ N4: `payroll_run.generated_by` — `auth.getName()`, ya'ni aktyor
  //    identifikatori (`PayrollServiceImpl` javadoc'i buni ochiq yozadi).
  const actors = useActorDirectory()

  const { state: tableState, patch: patchTable } = useTableUrlState()
  const year = intFilter(tableState.filters['year'], YEAR_MIN, YEAR_MAX)
  const month = intFilter(tableState.filters['month'], 1, 12)
  const state = runStateFilter(tableState.filters['state'])

  const [generateOpen, setGenerateOpen] = useState(false)

  const nodeSource = useEmployeeNodes(can('hr.payroll.run'))
  const centers = useMemo(
    () => costCenterNodes(nodeSource.nodes, nodeSource.types),
    [nodeSource.nodes, nodeSource.types],
  )

  const filter = useMemo(() => ({ year, month, state }), [year, month, state])

  // Filtr o'zgarganda sahifa BIRINCHIGA qaytadi — aks holda 5-sahifada
  // turib filtr qo'yilsa natija 2 sahifa bo'lganda ekran bo'sh chiqardi.
  const commitFilters = (next: Filters) => patchTable({ page: 1, filters: next })

  const columns: ColumnsType<PayrollRun> = [
    {
      title: t('hr.payroll.period'),
      key: 'period',
      render: (_value, row) => (
        <Space size={6} wrap>
          <Typography.Link onClick={() => void navigate(`/hr/payroll/${row.id}`)}>
            {periodLabel(row)}
          </Typography.Link>
          {isSelfApproval(row, user?.username ?? null) ? (
            <Tag color="gold">{t('hr.payroll.mine')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('hr.payroll.orgNode'),
      key: 'orgNode',
      render: (_value, row) => row.orgNodeName ?? row.orgNodeCode ?? '—',
    },
    {
      title: t('hr.payroll.state'),
      key: 'state',
      render: (_value, row) => (
        <Tag color={PAYROLL_STATE_COLOR[row.state]}>{t(`payrollState.${row.state}`)}</Tag>
      ),
    },
    {
      title: t('hr.payroll.lineCount'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      align: 'right',
    },
    {
      title: t('hr.payroll.earning'),
      key: 'earning',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.earning),
    },
    {
      title: t('hr.payroll.deduction'),
      key: 'deduction',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.deduction),
    },
    {
      title: t('hr.payroll.net'),
      key: 'net',
      align: 'right',
      render: (_value, row) => (
        <Typography.Text strong>{formatMoneyValue(row.net)}</Typography.Text>
      ),
    },
    {
      title: t('hr.payroll.generatedBy'),
      key: 'generatedBy',
      render: (_value, row) => actorLabel(row.generatedBy, actors, t('common.dash')),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.hrPayroll')}
        description={t('pageIntro.hrPayroll')}
        extra={
          <Can permission="hr.payroll.run">
            <Button type="primary" onClick={() => setGenerateOpen(true)}>
              {t('hr.payroll.generateAction')}
            </Button>
          </Can>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.payroll.listHint')} fontSize={13} />
      </div>

      <DataTable<PayrollRun>
        queryKey={[...queryKeys.hr.payrollRuns, filter]}
        fetchPage={fetchPayrollRunPage(filter)}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="hr.payrollRuns"
        emptyTitle={t('hr.payroll.empty')}
        emptyDescription={t('hr.payroll.emptyHint')}
        toolbarExtra={
          <Space wrap>
            {/* Holat tashqarida: «qaysi vedomost tasdiqlashimni kutyapti»
                bu sahifadagi eng ko'p beriladigan savol. */}
            <Select<PayrollRunState | null>
              allowClear
              placeholder={t('hr.payroll.state')}
              value={state}
              onChange={(value) => commitFilters({ ...tableState.filters, state: value ? [value] : [] })}
              style={{ width: 170 }}
              options={PAYROLL_RUN_STATES.map((value) => ({
                value,
                label: t(`payrollState.${value}`),
              }))}
            />
            <FilterDrawer<Filters>
              value={tableState.filters}
              resetValue={{ ...tableState.filters, year: [], month: [] }}
              activeCount={[year, month].filter((value) => value !== null).length}
              onApply={commitFilters}
            >
              {(draft, patchDraft) => (
                <>
                  <Form.Item label={t('hr.payroll.year')}>
                    <InputNumber
                      placeholder={t('hr.payroll.year')}
                      min={YEAR_MIN}
                      max={YEAR_MAX}
                      precision={0}
                      value={intFilter(draft['year'], YEAR_MIN, YEAR_MAX)}
                      onChange={(value) =>
                        patchDraft({ year: value === null ? [] : [String(value)] })
                      }
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label={t('hr.payroll.month')}>
                    <InputNumber
                      placeholder={t('hr.payroll.month')}
                      min={1}
                      max={12}
                      precision={0}
                      value={intFilter(draft['month'], 1, 12)}
                      onChange={(value) =>
                        patchDraft({ month: value === null ? [] : [String(value)] })
                      }
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </>
              )}
            </FilterDrawer>
          </Space>
        }
      />

      <PayrollGenerateModal
        open={generateOpen}
        nodes={centers}
        nodesReliable={nodeSource.reliable}
        onClose={() => setGenerateOpen(false)}
        onDone={(run) => void navigate(`/hr/payroll/${run.id}`)}
      />
    </>
  )
}
